<template>
    <side-panel class="side-panel" id="vehicles-passage-panel">
        <template #head>
            <div class="d-head">
                <div class="container-fluid">
                    <h3 class="panel-title">
                        {{ __('vehicles_cross.title') }}
                    </h3>

                    <div @click="$emit('toggleUtilityPanel')" class="close-btn">
                        <i class="fa-solid fa-xmark"></i>
                    </div>
                </div>
            </div>

            <div class="d-flex align-items-center justify-content-between mt-3 mb-4">
                <golia-button
                    variant="btn-danger"
                    icon="fa-fw fa-times"
                    style="margin-right: 5px"
                    :label="__('cancel_search')"
                    v-if="activeSearch && resultVehicles.length > 0"
                    @click="cancelSearch"
                />
                <golia-button
                    variant="btn-fill dark-blue"
                    icon="fa-fw fa-arrow-left"
                    :label="mode == 'results' ? __('edit_search') : __('back_to_results')"
                    style="margin-right: 5px"
                    v-if="activeSearch && resultVehicles.length > 0"
                    @click="mode == 'search' ? (mode = 'results') : (mode = 'search')"
                />
            </div>
        </template>

        <template #body>
            <div class="container-fluid">
                <template v-if="mode == 'search'">
                    <!-- FORM + EMPTY STATE -->
                    <vehicles-cross-form
                        v-model="vehiclesCross"
                        :isMoreThan24Hours="isMoreThan24Hours"
                        :loading="loading"
                        @searchVehicleCross="searchVehicleCross"
                    />

                    <!-- Empty results -->
                    <template v-if="activeSearch && resultVehicles.length === 0">
                        <div
                            class="no-vehicles d-flex align-items-center justify-content-center bg-main-gray mt-4"
                        >
                            <div class="text-center mt-3">
                                <i
                                    class="fa-solid fa-circle-exclamation fa-xl text-muted text-dark-blue"
                                ></i>
                                <p class="mt-3 text-muted">{{ __('no_vehicles_founded') }}</p>
                            </div>
                        </div>
                    </template>

                    <!-- Is loading message -->
                    <transition name="common-fade">
                        <template v-if="long_loading">
                            <div
                                class="no-vehicles d-flex align-items-center justify-content-center bg-main-gray mt-4"
                            >
                                <div class="text-center mt-3">
                                    <i
                                        class="fa-solid fa-circle-exclamation fa-xl text-muted text-dark-blue"
                                    ></i>
                                    <p class="mt-3 text-muted">
                                        {{ __('vehicles_cross.messages.long_loading') }}
                                    </p>
                                </div>
                            </div>
                        </template>
                    </transition>
                </template>
                <template v-else>
                    <!-- SEARCH RESULTS -->
                    <vehicles-cross-results :vehicles="resultVehicles" />
                </template>
            </div>
        </template>
    </side-panel>
</template>

<script>
import SidePanel from './SidePanel.vue'
import VehiclesCrossForm from './VehiclesCross/VehiclesCrossForm.vue'
import VehiclesCrossResults from './VehiclesCross/VehiclesCrossResults.vue'

import { mapState, mapMutations } from 'vuex'

export default {
    name: 'VehiclesCross',
    components: {
        SidePanel,
        VehiclesCrossForm,
        VehiclesCrossResults
    },
    data() {
        return {
            loading: false,
            long_loading: false,
            vehiclesCross: {
                address: null,
                lat: null,
                lng: null,
                radius: 0.1,
                timeInterval: {
                    start: this.moment().startOf('day'),
                    end: this.moment().endOf('day')
                }
            },
            activeSearch: false,
            resultVehicles: [],
            drawCirclesTimeout: null,
            mode: 'search',
            // Handle for the "taking a while" delayed message, cleared whenever
            // the request settles or is cancelled.
            longLoadingTimeout: null,
            // In-flight vehiclepassage request, cancelled when the location
            // changes or the panel closes.
            searchController: null
        }
    },
    created() {
        // Seed from the map context menu ("see vehicles history"): prefill the
        // address without entering the active-search / results state. The deep
        // watcher on `vehiclesCross` then draws the marker + flies to the point.
        if (this.consumeVehiclesCrossSeed()) return

        if (Object.values(this.vehiclesCrossSearch).length > 0) {
            this.activeSearch = true
            this.vehiclesCross = this.vehiclesCrossSearch
        }

        // If this.vehiclesCrossSearch.address and this.vehiclesCrossSearch.radius are defined, draw the circle
        if (this.vehiclesCrossSearch.address && this.vehiclesCrossSearch.radius) {
            let coordinates = [
                this.vehiclesCrossSearch.address.referenceCoordinate.y,
                this.vehiclesCrossSearch.address.referenceCoordinate.x
            ]
            this.$store.dispatch('drawCircle', {
                ref: 'vehicles-cross',
                center: coordinates,
                radius: this.vehiclesCrossSearch.radius * 1000
            })
        }

        if (this.vehiclesCrossResults.length > 0) this.resultVehicles = this.vehiclesCrossResults
    },
    methods: {
        // Consume a one-shot seed from the map context menu. Called both on
        // mount (created) and reactively (seed watcher) so a new point picked
        // while the panel is already open still updates the location. Returns
        // true when a seed was consumed.
        consumeVehiclesCrossSeed() {
            if (!this.vehiclesCrossSeed?.address) return false

            this.vehiclesCross = { ...this.vehiclesCross, address: this.vehiclesCrossSeed.address }
            this.clearVehiclesCrossSeed()
            return true
        },
        // Clear all loading state: cancel the long-loading timer and reset the
        // loading flags. Single place that ends a request's loading lifecycle.
        stopLoading() {
            if (this.longLoadingTimeout) {
                clearTimeout(this.longLoadingTimeout)
                this.longLoadingTimeout = null
            }
            this.loading = false
            this.long_loading = false
        },
        // Cancel any in-flight vehiclepassage request and reset loading state.
        abortSearch() {
            if (this.searchController) {
                this.searchController.abort()
                this.searchController = null
            }
            this.stopLoading()
        },
        searchVehicleCross(prepared_search_params) {
            if (this.isMoreThan24Hours) {
                this.$snotify.error(this.__('vehicles_cross.fields.time_interval_description'), {
                    position: 'centerTop'
                })
                return
            }

            if (this.loading) return

            // Cancel a stale in-flight request before starting a new one.
            this.abortSearch()
            this.searchController = new AbortController()

            this.loading = true

            this.longLoadingTimeout = setTimeout(() => {
                this.long_loading = true
            }, 10000)

            let search_url = this.$env.GOLIA_API + '/geofleet/vehiclepassage'

            let search_params = prepared_search_params || {
                start_date: this.startTimeInterval,
                end_date: this.endTimeInterval,
                lat: this.vehiclesCross.address.referenceCoordinate.y,
                lon: this.vehiclesCross.address.referenceCoordinate.x,
                radius: this.vehiclesCross.radius
            }

            // Save the search on the store
            this.setVehiclesCrossSearch(this.vehiclesCross)

            let search_url_params = new URLSearchParams(search_params).toString()

            this.activeSearch = false

            this.$fetch(search_url + '?' + search_url_params, {
                signal: this.searchController.signal
            })
                .then((response) => response.json())
                .then((data) => {
                    if (data.errors) {
                        this.$snotify.error(this.__('vehicles_cross.messages.error_searching'), {
                            position: 'centerTop'
                        })
                        return
                    }

                    this.resultVehicles = data.data
                    this.activeSearch = true

                    this.setVehiclesCrossResults(data.data)
                })
                .catch((error) => {
                    // Ignore cancellations triggered by a location change or the
                    // panel closing.
                    if (error?.name === 'AbortError') return

                    this.$snotify.error(this.__('vehicles_cross.messages.error_searching'), {
                        position: 'centerTop'
                    })
                })
                .finally(() => {
                    // AbortError already reset everything via abortSearch; a
                    // second stopLoading here is a harmless no-op.
                    this.searchController = null
                    this.stopLoading()
                })
        },
        editSearch() {
            this.mode = 'search'
        },
        cancelSearch() {
            this.resultVehicles = []
            this.vehiclesCross = {
                address: null,
                radius: 0.1,
                timeInterval: {
                    start: this.moment().startOf('day'),
                    end: this.moment().endOf('day')
                }
            }

            this.resetVehiclesCrossSearch()
            this.resetVehiclesCrossResults()

            this.activeSearch = false
            this.mode = 'search'

            this.$store.dispatch('removeCircle', { ref: 'vehicles-cross' })
            this.$store.dispatch('removeMarker', { ref: 'vehicles-cross' })
        },
        ...mapMutations({
            setVehiclesCrossSearch: 'setVehiclesCrossSearch',
            setVehiclesCrossResults: 'setVehiclesCrossResults',
            resetVehiclesCrossSearch: 'resetVehiclesCrossSearch',
            resetVehiclesCrossResults: 'resetVehiclesCrossResults',
            clearVehiclesCrossSeed: 'clearVehiclesCrossSeed'
        })
    },
    watch: {
        resultVehicles(val) {
            this.mode = val.length > 0 ? 'results' : 'search'
        },
        // A new point picked from the map context menu while the panel is
        // already open: consume the seed (updates the location) and drop the
        // user back on the search form.
        vehiclesCrossSeed(seed) {
            if (seed?.address && this.consumeVehiclesCrossSeed()) this.mode = 'search'
        },
        // Cancel any in-flight request when the searched location changes.
        vehiclesCrossLocationKey() {
            this.abortSearch()
        },
        vehiclesCross: {
            handler(val) {
                if (!val) return

                if (!val.address) {
                    this.$store.dispatch('removeMarker', { ref: 'vehicles-cross' })
                    this.$store.dispatch('removeCircle', { ref: 'vehicles-cross' })

                    this.$store.commit('setMainMapStopAutocenter', false)
                    return
                }

                // Address handler
                if (
                    val?.address?.referenceCoordinate?.x === null ||
                    val?.address?.referenceCoordinate?.y === null
                ) {
                    this.$store.dispatch('removeCircle', { ref: 'vehicles-cross' })

                    this.$store.commit('setMainMapStopAutocenter', false)
                    return
                }

                let coordinates = [
                    val.address.referenceCoordinate.y,
                    val.address.referenceCoordinate.x
                ]
                this.$store.dispatch('drawMarker', {
                    ref: 'vehicles-cross',
                    center: coordinates,
                    title: val.address.label
                })
                this.$store.dispatch('flyToMap', { center: coordinates, zoom: 15 })

                this.$store.commit('setMainMapStopAutocenter', true)

                // Radius handler
                if (!val.radius) return

                // Debouce the circle drawing
                if (this.drawCirclesTimeout) clearTimeout(this.drawCirclesTimeout)

                this.drawCirclesTimeout = setTimeout(() => {
                    this.$store.dispatch('drawCircle', {
                        ref: 'vehicles-cross',
                        center: coordinates,
                        radius: val.radius * 1000
                    })
                }, 300)
            },
            deep: true
        }
    },
    computed: {
        startTimeInterval() {
            return this.vehiclesCross?.timeInterval?.start?.clone().utc().format()
        },
        endTimeInterval() {
            return this.vehiclesCross?.timeInterval?.end?.clone().utc().format()
        },
        // Stable key for the searched point; changes whenever the coordinates
        // change, driving the request-cancellation watcher.
        vehiclesCrossLocationKey() {
            const coordinate = this.vehiclesCross?.address?.referenceCoordinate
            return coordinate ? `${coordinate.x},${coordinate.y}` : null
        },
        isMoreThan24Hours() {
            if (!this.vehiclesCross?.timeInterval?.start || !this.vehiclesCross?.timeInterval?.end)
                return false

            return (
                this.vehiclesCross.timeInterval.end.diff(
                    this.vehiclesCross.timeInterval.start,
                    'hours'
                ) > 24
            )
        },
        ...mapState({
            vehiclesCrossSearch: (state) => state.geoFleet.vehiclesCrossSearch,
            vehiclesCrossResults: (state) => state.geoFleet.vehiclesCrossResults,
            vehiclesCrossSeed: (state) => state.geoFleet.vehiclesCrossSeed
        })
    },
    beforeDestroy() {
        // Panel closed: cancel any request still in flight.
        this.abortSearch()

        this.$store.dispatch('removeCircle', { ref: 'vehicles-cross' })
        this.$store.dispatch('removeMarker', { ref: 'vehicles-cross' })

        this.$store.commit('setMainMapStopAutocenter', false)
    }
}
</script>

<style scoped lang="scss">
@use './SidePanel.scss';

#vehicles-passage-panel ::v-deep(.head),
#vehicles-passage-panel ::v-deep(.body) {
    --side-panels-security-width: 450px;
    width: var(--side-panels-security-width);
}
.no-vehicles {
    border-radius: 25px;
    font-size: 14px;
}
</style>
